import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Compass } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const STATUS_COLORS = {
  discovered: "bg-sky-50 text-sky-800 border-sky-200",
  reviewed: "bg-amber-50 text-amber-800 border-amber-200",
  applied: "bg-violet-50 text-violet-800 border-violet-200",
  approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
  live: "bg-emerald-50 text-emerald-800 border-emerald-200",
  rejected: "bg-rose-50 text-rose-800 border-rose-200",
  needs_followup: "bg-amber-50 text-amber-800 border-amber-200",
};

export default function AffiliateHunter() {
  const [apps, setApps] = useState([]);
  const [running, setRunning] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/affiliate-applications");
    setApps(data);
  };
  useEffect(() => { load(); }, []);

  const run = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/affiliate-hunter/run");
      toast.success(`AI Hunter discovered ${data.count} new prospects`);
      load();
    } catch (e) {
      toast.error("Hunter run failed");
    } finally {
      setRunning(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/admin/affiliate-applications/${id}/status?status=${status}`);
      toast.success(`Updated to ${status}`);
      load();
    } catch (e) {
      toast.error("Couldn't update");
    }
  };

  return (
    <div data-testid="adm-hunter-page">
      <PageHeader
        eyebrow="AI Affiliate Hunter"
        title="Discover new affiliate programs"
        subtitle="The AI scouts merchants in your underserved categories and drafts applications."
        action={<Button onClick={run} disabled={running} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full" data-testid="run-hunter"><Sparkles className="w-4 h-4 mr-2" /> {running ? "Hunting…" : "Run AI Hunter"}</Button>}
      />

      <Card className="border-gray-200 shadow-none rounded-2xl">
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-b border-gray-200 bg-gray-50">
              <tr className="text-left">
                <th className="px-5 py-3 font-semibold">Merchant</th>
                <th className="px-5 py-3 font-semibold">Network</th>
                <th className="px-5 py-3 font-semibold">Category</th>
                <th className="px-5 py-3 font-semibold">Est. Commission</th>
                <th className="px-5 py-3 font-semibold">Status</th>
                <th className="px-5 py-3 font-semibold w-44">Actions</th>
              </tr>
            </thead>
            <tbody>
              {apps.map((a) => (
                <tr key={a.id} className="border-b border-gray-100" data-testid={`app-${a.id}`}>
                  <td className="px-5 py-3 font-medium">
                    {a.merchant_name}
                    {a.notes && <div className="text-xs text-gray-500 mt-0.5">{a.notes}</div>}
                  </td>
                  <td className="px-5 py-3 text-gray-600">{a.network}</td>
                  <td className="px-5 py-3 capitalize text-gray-600">{a.category}</td>
                  <td className="px-5 py-3 text-emerald-700 font-semibold">{a.estimated_commission_rate}%</td>
                  <td className="px-5 py-3"><Badge className={`${STATUS_COLORS[a.status] || ""} border capitalize`}>{a.status.replace("_", " ")}</Badge></td>
                  <td className="px-5 py-3">
                    <Select onValueChange={(v) => updateStatus(a.id, v)}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Change status" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="reviewed">Mark reviewed</SelectItem>
                        <SelectItem value="applied">Applied</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="live">Live</SelectItem>
                        <SelectItem value="needs_followup">Needs follow-up</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                </tr>
              ))}
              {apps.length === 0 && (
                <tr><td colSpan="6" className="px-5 py-12 text-center">
                  <Compass className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <div className="text-gray-500">No applications yet. Run the AI Hunter to discover prospects.</div>
                </td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
